import { Link, useLocation } from 'react-router-dom';
import {
  Building2,
  FileSpreadsheet,
  GitBranch,
  Globe,
  Grid3X3,
  Home,
  LayoutGrid,
  Monitor,
  Plus,
  Shield,
  TreePine,
  Users,
  Zap,
} from 'lucide-react';
import { useSpaces } from '@/hooks/queries/useSpaces';
import { useObjects } from '@/hooks/queries/useObjects';
import { useUiStore } from '@/stores/ui.store';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import type { ObjectType } from '@arkitekt/shared';

const objectTypeIcons: Record<ObjectType, React.ReactNode> = {
  Application: <Monitor className="h-4 w-4" />,
  Technology: <LayoutGrid className="h-4 w-4" />,
  BusinessCapability: <Building2 className="h-4 w-4" />,
  Person: <Users className="h-4 w-4" />,
  Organization: <Globe className="h-4 w-4" />,
};

export function Sidebar() {
  const location = useLocation();
  const { sidebarOpen } = useUiStore();
  const { user } = useAuthStore();
  const { data: spacesData, isLoading: spacesLoading } = useSpaces();
  
  // Parse spaceKey from URL since Sidebar is outside route context
  const spaceKeyFromUrl = location.pathname.match(/^\/spaces\/([^/]+)/)?.[1];
  
  const spaces = spacesData?.data || [];
  const currentSpace = spaces.find((s) => s.key === spaceKeyFromUrl);
  
  const { data: objectsData, isLoading: objectsLoading } = useObjects(
    currentSpace?.id,
    { limit: 20 },
  );

  if (!sidebarOpen) {
    return null;
  }

  const objects = objectsData?.data || [];

  return (
    <aside className="flex h-full w-64 flex-col border-r bg-background">
      {/* Spaces header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h2 className="text-sm font-semibold text-muted-foreground">Arbeidsområder</h2>
        <Button variant="ghost" size="icon" className="h-7 w-7">
          <Plus className="h-4 w-4" />
          <span className="sr-only">Nytt arbeidsområde</span>
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2">
        {/* Home link */}
        <Link
          to="/spaces"
          className={cn(
            'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent',
            location.pathname === '/spaces' && 'bg-accent',
          )}
        >
          <Home className="h-4 w-4" />
          Alle arbeidsområder
        </Link>

        {/* Spaces list */}
        {spacesLoading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner size="sm" />
          </div>
        ) : (
          <div className="mt-4 space-y-1">
            {spaces.map((space) => (
              <div key={space.id}>
                <Link
                  to={`/spaces/${space.key}`}
                  className={cn(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent',
                    spaceKeyFromUrl === space.key && 'bg-accent font-medium',
                  )}
                >
                  <span className="text-lg">{space.iconEmoji || '📁'}</span>
                  <span className="flex-1 truncate">{space.name}</span>
                  {space.isPublic && <Globe className="h-3 w-3 text-muted-foreground" />}
                </Link>

                {/* Objects tree for selected space */}
                {spaceKeyFromUrl === space.key && currentSpace && (
                  <div className="ml-4 mt-1 border-l pl-2">
                    {/* Visualization links */}
                    <div className="space-y-0.5 mb-2">
                      <Link
                        to={`/spaces/${space.key}/graph`}
                        className={cn(
                          'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent',
                          location.pathname.includes('/graph') && 'bg-accent font-medium',
                        )}
                      >
                        <GitBranch className="h-4 w-4" />
                        <span className="truncate">Avhengighetsgraf</span>
                      </Link>
                      <Link
                        to={`/spaces/${space.key}/capabilities`}
                        className={cn(
                          'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent',
                          location.pathname.includes('/capabilities') && 'bg-accent font-medium',
                        )}
                      >
                        <TreePine className="h-4 w-4" />
                        <span className="truncate">Kapabilitetskart</span>
                      </Link>
                      <Link
                        to={`/spaces/${space.key}/matrix`}
                        className={cn(
                          'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent',
                          location.pathname.includes('/matrix') && 'bg-accent font-medium',
                        )}
                      >
                        <Grid3X3 className="h-4 w-4" />
                        <span className="truncate">Teknologimatrise</span>
                      </Link>
                      <Link
                        to={`/spaces/${space.key}/impact`}
                        className={cn(
                          'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent',
                          location.pathname.includes('/impact') && 'bg-accent font-medium',
                        )}
                      >
                        <Zap className="h-4 w-4" />
                        <span className="truncate">Impact-analyse</span>
                      </Link>
                      <Link
                        to={`/spaces/${space.key}/import`}
                        className={cn(
                          'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent',
                          location.pathname.includes('/import') && 'bg-accent font-medium',
                        )}
                      >
                        <FileSpreadsheet className="h-4 w-4" />
                        <span className="truncate">Importer data</span>
                      </Link>
                    </div>
                    <div className="border-t my-2" />
                    {objectsLoading ? (
                      <div className="flex justify-center py-2">
                        <LoadingSpinner size="sm" />
                      </div>
                    ) : objects.length === 0 ? (
                      <p className="px-2 py-2 text-xs text-muted-foreground">Ingen objekter ennå</p>
                    ) : (
                      <div className="space-y-0.5">
                        {objects.map((obj) => (
                          <Link
                            key={obj.id}
                            to={`/spaces/${space.key}/objects/${obj.id}`}
                            className={cn(
                              'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent',
                              location.pathname.includes(obj.id) && 'bg-accent font-medium',
                            )}
                          >
                            {objectTypeIcons[obj.type]}
                            <span className="flex-1 truncate">{obj.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t p-4">
        {user?.role === 'admin' && (
          <Link
            to="/admin/users"
            className={cn(
              'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent mb-2',
              location.pathname.startsWith('/admin') && 'bg-accent',
            )}
          >
            <Shield className="h-4 w-4" />
            Administrasjon
          </Link>
        )}
        <p className="text-xs text-muted-foreground">
          {spaces.length} arbeidsområde{spaces.length !== 1 && 'r'}
        </p>
      </div>
    </aside>
  );
}
